import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { stockLevelClass, computeM2Kg } from "../lib/display";
import { useAuth } from "../lib/auth";
import useAutoRefresh from "../lib/useAutoRefresh";
import { Search, Package, Trash2, X, Tag, Grid3x3, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { EditableDateCell } from "../components/EditableDateCell";

const PAGE_LIMIT = 200;

export default function Inventory() {
  const { user } = useAuth();
  const isAdmin = ["admin", "supervisor"].includes(user?.role);
  const isSupervisor = user?.role === "supervisor";
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [sehpa, setSehpa] = useState(() => searchParams.get("sehpa") || "");
  const [sehpaPrefix, setSehpaPrefix] = useState(() => searchParams.get("sehpa_prefix") || "");
  const [firmaFilter, setFirmaFilter] = useState(() => searchParams.get("firma") || "");
  const [isim, setIsim] = useState("");
  const [ebat, setEbat] = useState("");
  const [kalite, setKalite] = useState("");
  const [adetBucket, setAdetBucket] = useState(() => searchParams.get("adet") || "");
  const [includeEmpty, setIncludeEmpty] = useState(
    () => searchParams.get("include_empty") !== "0",
  );
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [shelves, setShelves] = useState([]);
  const [facets, setFacets] = useState({ isim: [], ebat: [], urun_kalitesi: [] });
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [brandList, setBrandList] = useState([]);
  const [brandAssignOpen, setBrandAssignOpen] = useState(false);
  const [brandAssignValue, setBrandAssignValue] = useState("");
  const [brandAssigning, setBrandAssigning] = useState(false);
  // Inline-edit state for the Açıklama (description) column. Any logged-in
  // user may edit this free-text note; backend PUT /api/inventory/{id} is
  // already open to non-admins for every field except `isim`.
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingAciklama, setSavingAciklama] = useState(false);
  const [pendingExits, setPendingExits] = useState({});

  const loadPendingExits = () => {
    api.get("/approvals/pending-exits").then(({ data }) => setPendingExits(data)).catch(() => {});
  };
  useEffect(() => {
    loadPendingExits();
  }, []);
  useAutoRefresh(loadPendingExits, 60 * 1000);

  // Keep URL and dropdown in sync when user changes the shelf filter via UI
  useEffect(() => {
    const current = searchParams.get("sehpa") || "";
    if (current !== sehpa) {
      const next = new URLSearchParams(searchParams);
      if (sehpa) next.set("sehpa", sehpa);
      else next.delete("sehpa");
      setSearchParams(next, { replace: true });
    }
  }, [sehpa]);

  // Sync back if the URL param changes externally (e.g. arriving from Sehpalar page)
  useEffect(() => {
    const urlSehpa = searchParams.get("sehpa") || "";
    if (urlSehpa !== sehpa) setSehpa(urlSehpa);
    const urlPrefix = searchParams.get("sehpa_prefix") || "";
    if (urlPrefix !== sehpaPrefix) setSehpaPrefix(urlPrefix);
    const urlFirma = searchParams.get("firma") || "";
    if (urlFirma !== firmaFilter) setFirmaFilter(urlFirma);
  }, [searchParams]);

  useEffect(() => {
    api.get("/shelves").then(({ data }) => setShelves(data)).catch(() => {});
    api.get("/inventory/facets").then(({ data }) => setFacets(data)).catch(() => {});
    api.get("/stats/brands/list").then(({ data }) => setBrandList(data)).catch(() => {});
  }, []);

  const adetBucketToRange = (bucket) => {
    switch (bucket) {
      case "zero":
        return { adet_min: 0, adet_max: 0 };
      case "low":
        return { adet_min: 1, adet_max: 2 };
      case "mid":
        return { adet_min: 3, adet_max: 10 };
      case "high":
        return { adet_min: 11 };
      default:
        return {};
    }
  };

  const clearAllFilters = () => {
    setQ("");
    setSehpa("");
    setSehpaPrefix("");
    setFirmaFilter("");
    setIsim("");
    setEbat("");
    setKalite("");
    setAdetBucket("");
    setIncludeEmpty(false);
    const next = new URLSearchParams(searchParams);
    next.delete("firma");
    next.delete("sehpa");
    next.delete("sehpa_prefix");
    next.delete("adet");
    next.delete("include_empty");
    setSearchParams(next, { replace: true });
  };

  const activeFilters =
    (q ? 1 : 0) +
    (sehpa ? 1 : 0) +
    (sehpaPrefix ? 1 : 0) +
    (firmaFilter ? 1 : 0) +
    (isim ? 1 : 0) +
    (ebat ? 1 : 0) +
    (kalite ? 1 : 0) +
    (adetBucket ? 1 : 0);

  const reload = () => {
    setLoading(true);
    api
      .get("/inventory", {
        params: {
          q,
          sehpa,
          sehpa_prefix: sehpaPrefix,
          firma: firmaFilter,
          isim,
          ebat,
          kalite,
          include_empty: includeEmpty,
          limit: PAGE_LIMIT,
          ...adetBucketToRange(adetBucket),
        },
      })
      .then(({ data }) => {
        setData(data);
        setSelected(new Set());
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/inventory", {
          params: {
            q,
            sehpa,
            sehpa_prefix: sehpaPrefix,
            firma: firmaFilter,
            isim,
            ebat,
            kalite,
            include_empty: includeEmpty,
            limit: PAGE_LIMIT,
            ...adetBucketToRange(adetBucket),
          },
        })
        .then(({ data }) => {
          if (!cancelled) {
            setData(data);
            setSelected(new Set());
          }
        })
        .catch((e) => !cancelled && toast.error(formatApiError(e)))
        .finally(() => !cancelled && setLoading(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, sehpa, sehpaPrefix, firmaFilter, isim, ebat, kalite, adetBucket, includeEmpty]);

  useAutoRefresh(reload);

  const displayed = data.items;
  const allSelected = useMemo(
    () => displayed.length > 0 && displayed.every((it) => selected.has(it.id)),
    [displayed, selected],
  );

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(displayed.map((it) => it.id)));
  };

  const doDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      if (isSupervisor) {
        const { data: res } = await api.post("/inventory/bulk-delete", {
          ids: Array.from(selected),
          aciklama: "Envanterden toplu silme",
        });
        toast.success(`${res.deleted} kayıt silindi`);
      } else {
        const { data: res } = await api.post("/deletion-requests", {
          ids: Array.from(selected),
          reason: "",
        });
        toast.success(
          `Silme talebi oluşturuldu · ${res.count} kayıt Supervisor onayı bekliyor`,
        );
      }
      setConfirmOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const doAssignBrand = async () => {
    if (selected.size === 0) return;
    setBrandAssigning(true);
    try {
      const { data: res } = await api.post("/inventory/bulk-assign-brand", {
        ids: Array.from(selected),
        firma: brandAssignValue,
      });
      toast.success(
        brandAssignValue
          ? `${res.modified} kayıt "${brandAssignValue}" markasına atandı`
          : `${res.modified} kayıt marka ataması kaldırıldı`,
      );
      setBrandAssignOpen(false);
      setBrandAssignValue("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBrandAssigning(false);
    }
  };

  const startEditAciklama = (item) => {
    setEditingId(item.id);
    setEditingValue(item.aciklama || "");
  };

  const cancelEditAciklama = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const saveAciklama = async () => {
    if (!editingId) return;
    const id = editingId;
    const value = editingValue;
    const original = data.items.find((it) => it.id === id);
    if (original && (original.aciklama || "") === value) {
      cancelEditAciklama();
      return;
    }
    setSavingAciklama(true);
    try {
      const { data: updated } = await api.put(`/inventory/${id}`, { aciklama: value });
      setData((prev) => ({
        ...prev,
        items: prev.items.map((it) => (it.id === id ? { ...it, aciklama: updated.aciklama ?? value } : it)),
      }));
      toast.success("Açıklama güncellendi");
      cancelEditAciklama();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingAciklama(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-3)]"
          />
          <input
            data-testid="inventory-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Barkod, isim, ebat, sehpa, açıklama ara…"
            className="w-full h-11 pl-9 pr-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none"
          />
        </div>

        <select
          data-testid="inventory-shelf-filter"
          value={sehpa}
          onChange={(e) => setSehpa(e.target.value)}
          className="h-11 px-3 hard border-2 border-[color:var(--border)] bg-white outline-none focus:border-[color:var(--primary)] min-w-[140px]"
        >
          <option value="">Tüm Sehpalar</option>
          {shelves.map((s) => (
            <option key={s.sehpa} value={s.sehpa}>
              {s.sehpa} ({s.product_count})
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            data-testid="inventory-include-empty"
            type="checkbox"
            checked={includeEmpty}
            onChange={(e) => setIncludeEmpty(e.target.checked)}
            className="w-4 h-4 accent-[color:var(--primary)]"
          />
          <span>Boş stokları göster</span>
        </label>

        {firmaFilter && (
          <div
            data-testid="inventory-firma-chip"
            className="h-11 flex items-center gap-2 px-3 hard bg-[color:var(--primary)] text-white text-sm font-bold uppercase tracking-wider"
          >
            <Tag size={14} strokeWidth={2.5} />
            {firmaFilter}
            <button
              onClick={() => {
                setFirmaFilter("");
                const next = new URLSearchParams(searchParams);
                next.delete("firma");
                setSearchParams(next, { replace: true });
              }}
              className="ml-1 hover:opacity-70"
              aria-label="Firma filtresini kaldır"
              data-testid="inventory-firma-chip-clear"
            >
              <X size={14} strokeWidth={3} />
            </button>
          </div>
        )}

        {sehpaPrefix && (
          <div
            data-testid="inventory-sehpa-prefix-chip"
            className="h-11 flex items-center gap-2 px-3 hard bg-[color:var(--accent)] text-white text-sm font-bold uppercase tracking-wider"
          >
            <Grid3x3 size={14} strokeWidth={2.5} />
            Sehpa yanı: {sehpaPrefix}
            <button
              onClick={() => {
                setSehpaPrefix("");
                const next = new URLSearchParams(searchParams);
                next.delete("sehpa_prefix");
                setSearchParams(next, { replace: true });
              }}
              className="ml-1 hover:opacity-70"
              aria-label="Sehpa yanı filtresini kaldır"
              data-testid="inventory-sehpa-prefix-clear"
            >
              <X size={14} strokeWidth={3} />
            </button>
          </div>
        )}

        {activeFilters > 0 && (
          <button
            data-testid="inventory-clear-filters"
            onClick={clearAllFilters}
            className="h-11 px-3 hard border-2 border-[color:var(--accent)] text-[color:var(--accent)] hover:bg-[color:var(--accent)] hover:text-white text-sm font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
            title="Tüm filtreleri temizle"
          >
            <X size={14} strokeWidth={2.5} />
            {activeFilters} Filtre
          </button>
        )}

        <div className="ticker text-[color:var(--text-3)] ml-auto">
          {loading ? "Yükleniyor…" : `${data.total.toLocaleString("tr-TR")} kayıt`}
        </div>
      </div>

      {/* Bulk action bar — visible when items selected */}
      {selected.size > 0 && (
        <div
          data-testid="inventory-bulk-actionbar"
          className="hard border-2 border-[color:var(--primary)] bg-[color:var(--primary)] text-white p-3 flex items-center justify-between row-enter"
        >
          <div className="flex items-center gap-3">
            <span className="ticker">/// SEÇİLİ</span>
            <span className="font-mono font-bold text-lg">{selected.size}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 hard bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors"
            >
              Seçimi Temizle
            </button>
            {isAdmin && (
                <button
                  data-testid="inventory-bulk-assign-brand-btn"
                  onClick={() => setBrandAssignOpen(true)}
                  className="px-4 py-1.5 hard bg-white/15 hover:bg-white/25 text-white font-bold uppercase tracking-wider text-sm flex items-center gap-2 transition-colors"
                >
                  <Tag size={14} strokeWidth={2.5} /> Firma Ata
                </button>
            )}
            {isSupervisor ? (
                <button
                  data-testid="inventory-bulk-delete-btn"
                  onClick={() => setConfirmOpen(true)}
                  className="px-4 py-1.5 hard bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white font-bold uppercase tracking-wider text-sm flex items-center gap-2 transition-colors"
                >
                  <Trash2 size={14} strokeWidth={2.5} /> Toplu Sil
                </button>
            ) : (
              <button
                data-testid="inventory-request-delete-btn"
                onClick={() => setConfirmOpen(true)}
                className="px-4 py-1.5 hard bg-[color:var(--warning)] hover:opacity-90 text-white font-bold uppercase tracking-wider text-sm flex items-center gap-2 transition-colors"
                title="Supervisor onayı bekleyen bir silme talebi oluştur"
              >
                <Trash2 size={14} strokeWidth={2.5} /> Silme Talebi
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: "70vh" }}>
          <table className="dense-table w-full" data-testid="inventory-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    data-testid="inventory-select-all"
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="w-4 h-4 accent-[color:var(--primary)]"
                  />
                </th>
                <th>Barkod</th>
                <th>İsim</th>
                <th>Firma</th>
                <th>Ebat</th>
                <th>Adet</th>
                <th className="text-right">m²</th>
                <th className="text-right">KG</th>
                <th>Sehpa</th>
                <th>Kalite</th>
                <th>Geliş</th>
                <th>Açıklama</th>
              </tr>
              {/* Column-header dropdown filters — no typing needed */}
              <tr className="bg-[color:var(--bg)]">
                <th></th>
                <th></th>
                <th>
                  <select
                    data-testid="inventory-col-isim"
                    value={isim}
                    onChange={(e) => setIsim(e.target.value)}
                    className="w-full h-8 px-1.5 hard border border-[color:var(--border)] bg-white text-xs outline-none focus:border-[color:var(--primary)] max-w-[220px]"
                  >
                    <option value="">Tüm isimler ({facets.isim.length})</option>
                    {facets.isim.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </th>
                <th></th>
                <th>
                  <select
                    data-testid="inventory-col-ebat"
                    value={ebat}
                    onChange={(e) => setEbat(e.target.value)}
                    className="w-full h-8 px-1.5 hard border border-[color:var(--border)] bg-white text-xs outline-none focus:border-[color:var(--primary)] font-mono max-w-[140px]"
                  >
                    <option value="">Tüm ebatlar ({facets.ebat.length})</option>
                    {facets.ebat.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </th>
                <th>
                  <select
                    data-testid="inventory-col-adet"
                    value={adetBucket}
                    onChange={(e) => setAdetBucket(e.target.value)}
                    className="w-full h-8 px-1.5 hard border border-[color:var(--border)] bg-white text-xs outline-none focus:border-[color:var(--primary)]"
                  >
                    <option value="">Tüm adet</option>
                    <option value="zero">0 (boş)</option>
                    <option value="low">1–2 (düşük)</option>
                    <option value="mid">3–10</option>
                    <option value="high">11+</option>
                  </select>
                </th>
                <th></th>
                <th></th>
                <th></th>
                <th>
                  <select
                    data-testid="inventory-col-kalite"
                    value={kalite}
                    onChange={(e) => setKalite(e.target.value)}
                    className="w-full h-8 px-1.5 hard border border-[color:var(--border)] bg-white text-xs outline-none focus:border-[color:var(--primary)]"
                  >
                    <option value="">Tüm kalite</option>
                    {facets.urun_kalitesi.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 && !loading && (
                <tr>
                  <td colSpan={12} className="py-16 text-center text-[color:var(--text-3)]">
                    <Package size={32} className="mx-auto mb-2 opacity-40" />
                    Kayıt bulunamadı.
                  </td>
                </tr>
              )}
              {displayed.map((item) => {
                const checked = selected.has(item.id);
                const { m2, kg, hasDims, hasThickness } = computeM2Kg(item);
                const isEditing = editingId === item.id;
                return (
                  <tr
                    key={item.id}
                    data-testid="inventory-table-row"
                    className={checked ? "bg-[color:var(--accent-bg)]" : ""}
                  >
                    <td>
                      <input
                        data-testid="inventory-row-checkbox"
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(item.id)}
                        className="w-4 h-4 accent-[color:var(--primary)]"
                      />
                    </td>
                    <td className="font-mono font-semibold">
                      <div className="flex items-center gap-1.5">
                        <span>{item.barkod}</span>
                        {pendingExits[item.barkod] > 0 && (
                          <span
                            data-testid="inventory-row-pending-tag"
                            title={`${pendingExits[item.barkod]} adet çıkış onayı bekliyor`}
                            className="px-1.5 py-0.5 hard text-[9px] font-bold uppercase tracking-wider bg-[color:var(--warning-bg)] text-[color:var(--warning)] border border-[color:var(--warning)] whitespace-nowrap"
                          >
                            Onayda · {pendingExits[item.barkod]}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{item.isim}</td>
                    <td>
                      {item.firma ? (
                        <span
                          data-testid="inventory-row-firma"
                          className="inline-block px-1.5 py-0.5 hard bg-[color:var(--primary)] text-white font-mono text-[10px] font-bold uppercase"
                        >
                          {item.firma}
                        </span>
                      ) : (
                        <span className="text-[color:var(--text-3)] text-xs">—</span>
                      )}
                    </td>
                    <td className="font-mono text-[color:var(--text-2)]">{item.ebat}</td>
                    <td>
                      <span className={`font-mono font-bold ${stockLevelClass(item.adet)}`}>
                        {item.adet}
                      </span>
                    </td>
                    <td
                      className="font-mono text-right text-xs tabular-nums"
                      data-testid="inventory-row-m2"
                      title={hasDims ? `${m2} m²` : "Ebat çözümlenemedi"}
                    >
                      {hasDims ? m2.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : <span className="text-[color:var(--text-3)]">—</span>}
                    </td>
                    <td
                      className="font-mono text-right text-xs tabular-nums"
                      data-testid="inventory-row-kg"
                      title={hasThickness ? `${kg} kg` : "Kalınlık (mm) isimde yok"}
                    >
                      {hasThickness && hasDims ? kg.toLocaleString("tr-TR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : <span className="text-[color:var(--text-3)]">—</span>}
                    </td>
                    <td>
                      <span className="inline-block px-2 py-0.5 hard bg-[color:var(--bg)] font-mono text-xs font-semibold">
                        {item.sehpa_konumu || "-"}
                      </span>
                    </td>
                    <td className="text-xs">{item.urun_kalitesi}</td>
                    <td className="text-xs text-[color:var(--text-2)] whitespace-nowrap" data-testid="inventory-row-date">
                      <EditableDateCell
                        item={item}
                        isSupervisor={isSupervisor}
                        onUpdated={(u) =>
                          setData((prev) => ({
                            ...prev,
                            items: prev.items.map((it) => (it.id === u.id ? { ...it, gelis_tarihi: u.gelis_tarihi } : it)),
                          }))
                        }
                      />
                    </td>
                    <td
                      className="text-xs text-[color:var(--text-2)]"
                      data-testid="inventory-row-aciklama"
                    >
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <input
                            data-testid="inventory-row-aciklama-input"
                            autoFocus
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveAciklama();
                              else if (e.key === "Escape") cancelEditAciklama();
                            }}
                            disabled={savingAciklama}
                            placeholder="Açıklama…"
                            className="flex-1 h-7 px-2 hard border border-[color:var(--primary)] bg-white text-xs outline-none"
                          />
                          <button
                            data-testid="inventory-row-aciklama-save"
                            onClick={saveAciklama}
                            disabled={savingAciklama}
                            title="Kaydet (Enter)"
                            className="p-1 hard bg-[color:var(--success)] text-white hover:opacity-90 disabled:opacity-50"
                          >
                            <Check size={12} strokeWidth={3} />
                          </button>
                          <button
                            data-testid="inventory-row-aciklama-cancel"
                            onClick={cancelEditAciklama}
                            disabled={savingAciklama}
                            title="İptal (Esc)"
                            className="p-1 hard border border-[color:var(--border)] hover:bg-[color:var(--bg)] disabled:opacity-50"
                          >
                            <X size={12} strokeWidth={3} />
                          </button>
                        </div>
                      ) : (
                        <button
                          data-testid="inventory-row-aciklama-edit"
                          onClick={() => startEditAciklama(item)}
                          title="Açıklamayı düzenle"
                          className="group inline-flex items-center gap-1.5 max-w-full text-left hover:text-[color:var(--primary)] transition-colors"
                        >
                          <span className="truncate max-w-[220px]">
                            {item.aciklama ? item.aciklama : <span className="italic text-[color:var(--text-3)]">+ ekle</span>}
                          </span>
                          <Pencil
                            size={11}
                            className="opacity-0 group-hover:opacity-100 text-[color:var(--text-3)] group-hover:text-[color:var(--primary)] transition-opacity shrink-0"
                          />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white hard border-2 border-[color:var(--border)]">
            <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between">
              <div>
                <div className="ticker text-[color:var(--error)]">
                  /// YÖNETİCİ ONAYI
                </div>
                <div className="font-bold text-lg mt-1">Toplu Silme</div>
              </div>
              <button
                onClick={() => setConfirmOpen(false)}
                className="p-2 hard hover:bg-[color:var(--bg)]"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm">
                <b className="text-[color:var(--error)]">{selected.size}</b> adet
                envanter kaydı ve stok bilgileri kalıcı olarak silinecek. Bu
                işlem geri alınamaz.
              </p>
              <div className="hard border border-[color:var(--warning)] bg-[color:var(--warning-bg)] text-[color:var(--warning)] p-3 text-xs">
                Silinen her kayıt hareket geçmişine (SİLME) not olarak eklenecektir.
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setConfirmOpen(false)}
                  disabled={deleting}
                  className="flex-1 h-11 hard border-2 border-[color:var(--border)] hover:bg-[color:var(--bg)] font-semibold disabled:opacity-50"
                >
                  Vazgeç
                </button>
                <button
                  data-testid="inventory-bulk-delete-confirm"
                  onClick={doDelete}
                  disabled={deleting}
                  className="flex-1 h-11 hard bg-[color:var(--error)] hover:opacity-90 text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50 transition-opacity"
                >
                  <Trash2 size={16} strokeWidth={2.5} />
                  {deleting ? "Siliniyor…" : "Evet, Sil"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign brand modal */}
      {brandAssignOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white hard border-2 border-[color:var(--border)]">
            <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between">
              <div>
                <div className="ticker text-[color:var(--primary)]">
                  /// FİRMA ATA
                </div>
                <div className="font-bold text-lg mt-1">
                  {selected.size} kayıt için marka seç
                </div>
              </div>
              <button
                onClick={() => setBrandAssignOpen(false)}
                className="p-2 hard hover:bg-[color:var(--bg)]"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-[color:var(--text-2)]">
                Seçilen kayıtlar aşağıdaki firmaya atanacak. Ürün ismi ne
                olursa olsun manuel atama, otomatik keyword eşleşmesini
                geçersiz kılar.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {brandList.map((b) => (
                  <button
                    key={b.firma}
                    data-testid={`brand-choice-${b.firma.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setBrandAssignValue(b.firma)}
                    className={`h-11 hard border-2 text-sm font-bold uppercase tracking-wider transition-colors ${
                      brandAssignValue === b.firma
                        ? "border-[color:var(--primary)] bg-[color:var(--primary)] text-white"
                        : "border-[color:var(--border)] bg-white hover:border-[color:var(--primary)]"
                    }`}
                  >
                    {b.firma}
                  </button>
                ))}
              </div>
              <button
                data-testid="brand-choice-clear"
                onClick={() => setBrandAssignValue("")}
                className={`w-full h-10 hard border-2 border-dashed text-xs font-semibold uppercase tracking-wider transition-colors ${
                  brandAssignValue === ""
                    ? "border-[color:var(--accent)] bg-[color:var(--accent-bg)] text-[color:var(--accent)]"
                    : "border-[color:var(--border)] text-[color:var(--text-2)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
                }`}
              >
                Marka atamasını kaldır
              </button>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setBrandAssignOpen(false)}
                  disabled={brandAssigning}
                  className="flex-1 h-11 hard border-2 border-[color:var(--border)] hover:bg-[color:var(--bg)] font-semibold disabled:opacity-50"
                >
                  Vazgeç
                </button>
                <button
                  data-testid="inventory-bulk-assign-brand-confirm"
                  onClick={doAssignBrand}
                  disabled={brandAssigning}
                  className="flex-1 h-11 hard bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-wider disabled:opacity-50 transition-colors"
                >
                  {brandAssigning ? "Atanıyor…" : "Uygula"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
